import React, { useState } from 'react';

import { StandardButton } from '@eika/button';
import { Input } from '@eika/input';
import { ErrorLabel } from '@eika/label';
import { ButtonToggles } from '@eika/radio-button';

import { DispatchWithState, InputChangeEvent, StringKeyValue } from '../../actions/types';
import { LoaderReducer } from '../../reducers/loader';
import { NafFormType, NafReducer } from '../../reducers/naf';
import { UserReducer } from '../../reducers/user';
import { formatNafMemberFullDate } from '../../util/dateUtil/dateUtil';
import Loading from '../Messages/Loading';

interface Props {
    action: {
        fetchNafMember: (form: NafFormType) => DispatchWithState;
    };
    loader: LoaderReducer;
    text: StringKeyValue;
    naf: NafReducer;
    user: UserReducer;
}

const regextTests: { [key: string]: (value: string) => boolean } = {
    phoneNumber: (value: string) => !/^\d{8}$/.test(value),
    memberNumber: (value: string) => !/^\d{7,8}$/.test(value),
    birthDate: (value: string) => !/^\d{8}$/.test(value),
};

const NafMemberCheckForm = (props: Props): JSX.Element => {
    const { loader, action, naf, text, user } = props;
    const { fetchNafMember } = action;
    const [formData, setFormData] = useState({
        firstField: '',
        birthDate: formatNafMemberFullDate(user.birthDate),
        selectedOption: 'phoneNumber',
    });

    const { firstField, birthDate, selectedOption } = formData;

    const [errors, setErrors] = useState<{ [key: string]: string }>({
        firstField: '',
        birthDate: '',
    });

    const handleChange = (e: InputChangeEvent) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: String(value),
        });
    };

    const authMethodErrors: { [key: string]: string } = {
        phoneNumber: text.nafErrorMobiltelefon,
        memberNumber: text.nafErrorMedlemsnummer,
    };

    const blurValidate = (e: InputChangeEvent) => {
        if (e.target.name === 'birthDate' && regextTests.birthDate(e.target.value))
            setErrors({ ...errors, birthDate: text.nafErrorFoedselsdato });
        else {
            setErrors({ ...errors, birthDate: '' });
        }
        if (regextTests[selectedOption](e.target.value))
            setErrors({ ...errors, firstField: authMethodErrors[selectedOption] });
        else {
            setErrors({ ...errors, firstField: '' });
        }
    };

    const handleAuthMethodChange = (e: string) => {
        setFormData({ ...formData, selectedOption: e, firstField: '' });
        setErrors({ firstField: '' });
    };

    const hasError = !!Object.keys(errors).find((k: string) => errors[k]);

    const validateForm = async () => {
        let formValid = true;
        if (regextTests.birthDate(birthDate)) {
            formValid = false;
            await setErrors({ ...errors, birthDate: text.nafErrorFoedselsdato });
        }
        if (regextTests[selectedOption](firstField)) {
            formValid = false;
            await setErrors({ ...errors, firstField: authMethodErrors[selectedOption] });
        }
        return formValid;
    };

    const handleSubmit = async () => {
        const formValid = await validateForm();
        if (formValid) {
            fetchNafMember({
                telefonnummer: selectedOption === 'phoneNumber' ? firstField : null,
                medlemsnummer: selectedOption === 'memberNumber' ? firstField : null,
                foedselsdato: birthDate.replace('-', ''),
            });
        }
    };

    return (
        <div className="credit-card-application">
            <div>
                <div className="credit-card-application-input">
                    <ButtonToggles
                        label=""
                        onChange={handleAuthMethodChange}
                        selectedOption={formData.selectedOption}
                        options={[
                            { value: 'phoneNumber', label: text.nafMedlemSjekkMobilnummer },
                            { value: 'memberNumber', label: text.nafMedlemSjekkMedlemsnummer },
                        ]}
                    />
                </div>
                <div className="credit-card-application-input">
                    <Input
                        value={formData.firstField}
                        name="firstField"
                        onChange={handleChange}
                        onBlur={blurValidate}
                        label={
                            formData.selectedOption === 'phoneNumber'
                                ? text.nafMedlemSjekkVerifiseringMobilnummerLabel
                                : text.nafMedlemSjekkVerifiseringMedlemsnummerLabel
                        }
                        error={errors.firstField}
                        disabled={loader.isFetchingNafMember}
                    />
                </div>
                <div className="credit-card-application-input">
                    <Input
                        autoComplete="off"
                        name="birthDate"
                        value={formData.birthDate}
                        onChange={handleChange}
                        onBlur={blurValidate}
                        label={text.nafMedlemSjekkFoedselsdatoLabel}
                        error={errors.birthDate}
                        disabled={loader.isFetchingNafMember}
                    />
                </div>

                {naf.errorMemberNoMatch && (
                    <div className="naf-member-check__error">
                        <ErrorLabel text={text.nafErrorIkkeMedlem} />
                    </div>
                )}

                {naf.errorMemberUnderAge && (
                    <div className="naf-member-check__error">
                        <ErrorLabel text={text.nafErrorIkkeGammelNok} />
                    </div>
                )}

                {naf.errorBadRequest && <ErrorLabel text={text.nafErrorBadRequest} />}

                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    {loader.isFetchingNafMember ? (
                        <Loading title={text.loadingNafCheck} />
                    ) : (
                        <StandardButton onClick={handleSubmit} disabled={hasError}>
                            {text.bankIdButton}
                        </StandardButton>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NafMemberCheckForm;
